"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActionMenu = void 0;
var react_1 = __importStar(require("react"));
var ButtonComps = __importStar(require("../Buttons"));
var Typo_1 = require("../Typo");
var icons_1 = require("../../icons");
var useHandleClickOutside_1 = require("../../utils/customHooks/useHandleClickOutside");
var usePopOutOfOverflowHiddenParent_1 = require("../../utils/usePopOutOfOverflowHiddenParent");
var styled_1 = require("./styled");
var ModalConfirmAction_1 = require("../Modals/derivedComps/ModalConfirmAction");
var react_dom_1 = require("react-dom");
var Tooltip_1 = require("../Tooltip");
/**
 * @component Renders an action menu component with customizable text and styling.
 *
 * - The props object containing the following properties:
 * @prop {string} icon: The name of the icon to be displayed. Defaults to "DotsThree".
 * @prop {boolean} invert: A boolean indicating whether to invert the action menu colors. Defaults to false.
 * @prop {ActionItem[]} actions: An array of action items to be displayed in the action menu.
 * @prop {"left" | "right" | "center"} align: The alignment of the action menu relative to its parent element. Defaults to "right".
 * @prop {"S" | "M"} iconSize: The size of the icon. Defaults to "S".
 * @prop {boolean} M: A boolean indicating whether to use the medium sized buttons. Defaults to false.
 * @prop {keyof typeof ButtonComps} ButtonComp: The name of the button component to use for each action item. Defaults to "ButtonGhost".
 * @prop {React.Dispatch<React.SetStateAction<boolean>>} setShowActions: A callback function to set the expanded state of the action menu.
 * @prop {boolean} popOutOfOverflowHiddenParent: A boolean indicating whether the action menu should pop out of an overflow:hidden parent element. Defaults to false.
 * @prop {React.ReactNode | React.FunctionComponent<CustomSelectCompProps>} CustomSelectComp: A custom select component to use instead of the default select component.
 * @prop {boolean} storybookPreview: A boolean indicating whether the component is being rendered in storybook. Defaults to false.
 * @return {ReactElement} The rendered action menu component.
 */
var ActionMenu = function (_a) {
    var _b = _a.icon, icon = _b === void 0 ? "DotsThree" : _b, invert = _a.invert, actions = _a.actions, _c = _a.align, align = _c === void 0 ? "right" : _c, _d = _a.iconSize, iconSize = _d === void 0 ? "S" : _d, M = _a.M, _e = _a.ButtonComp, _ButtonComp = _e === void 0 ? "ButtonGhost" : _e, setShowActionsInParent = _a.setShowActions, _f = _a.popOutOfOverflowHiddenParent, popOutOfOverflowHiddenParent = _f === void 0 ? false : _f, CustomSelectComp = _a.CustomSelectComp, storybookPreview = _a.storybookPreview;
    var actionMenuRef = (0, react_1.useRef)(null);
    var optionsWrapperRef = (0, react_1.useRef)(null);
    var _g = (0, react_1.useState)(false), showActions = _g[0], setShowActions = _g[1];
    (0, useHandleClickOutside_1.useHandleClickOutside)(popOutOfOverflowHiddenParent
        ? [actionMenuRef, optionsWrapperRef]
        : actionMenuRef, setShowActions);
    var getOpsRect = function () { var _a; return (_a = optionsWrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect(); };
    var ButtonComp = ButtonComps[_ButtonComp];
    var buttonSize = M ? { M: true } : {};
    var genOptionIcon = function (iconName) {
        var IconComp = icons_1.icons[iconName];
        return react_1.default.createElement(IconComp, { size: 16 });
    };
    (0, react_1.useEffect)(function () {
        setShowActionsInParent && setShowActionsInParent(showActions);
        if (popOutOfOverflowHiddenParent) {
            if (showActions)
                applyFixedPos();
            else
                removeFixedPos();
        }
    }, [showActions]);
    (0, react_1.useEffect)(function () {
        return function () {
            return setShowActionsInParent && setShowActionsInParent(false);
        };
    }, []);
    var _h = (0, usePopOutOfOverflowHiddenParent_1.usePopOutOfOverflowHiddenParent)(popOutOfOverflowHiddenParent), fixPos = _h.fixPos, applyFixedPos = _h.applyFixedPos, removeFixedPos = _h.removeFixedPos, trackerRef = _h.trackerRef;
    if (!actions || actions.length === 0)
        return null;
    var genLighbox = function () {
        var _a, _b;
        return (react_1.default.createElement(styled_1.StyledActionsDropMenu, { id: "dropmenu", align: align, invert: invert, optionsHeight: (_a = getOpsRect()) === null || _a === void 0 ? void 0 : _a.height, optionsWidth: (_b = getOpsRect()) === null || _b === void 0 ? void 0 : _b.width, showActions: showActions, fixPos: fixPos },
            react_1.default.createElement(styled_1.StyledActionMenuOpsWrapper, { invert: invert, align: align, ref: optionsWrapperRef }, actions.map(function (action) {
                var _a, _b, _c;
                var optionContent = (react_1.default.createElement(styled_1.StyledOption, __assign({ disabled: action.disabled }, action, { onClick: function (e) {
                        var _a;
                        e.preventDefault();
                        e.stopPropagation();
                        !action.disabled && ((_a = action.onClick) === null || _a === void 0 ? void 0 : _a.call(action, e));
                        setShowActions(false);
                    }, key: action.display, invert: invert }),
                    action.icon && genOptionIcon(action.icon),
                    react_1.default.createElement(Typo_1.SANS_2, null, action.display)));
                return (react_1.default.createElement(ModalConfirmAction_1.ModalConfirmAction, __assign({}, action.confirmAction, { key: action.display }), action.infoTooltip ? (react_1.default.createElement(Tooltip_1.Tooltip, { text: ((_a = action.infoTooltip) === null || _a === void 0 ? void 0 : _a.text) || undefined, popOutOfOverflowHiddenParent: ((_b = action.infoTooltip) === null || _b === void 0 ? void 0 : _b.popOutOfOverflowHiddenParent) || false, position: ((_c = action.infoTooltip) === null || _c === void 0 ? void 0 : _c.position) || "left", invert: invert }, optionContent)) : (optionContent)));
            }))));
    };
    return (react_1.default.createElement(styled_1.StyledActionMenu, { ref: actionMenuRef },
        react_1.default.createElement("div", { style: { zIndex: 1002, position: "relative" } }, typeof CustomSelectComp === 'function' ? (react_1.default.createElement("div", { onClick: function (e) {
                e.preventDefault();
                e.stopPropagation();
            }, onMouseDown: function (e) {
                e.preventDefault();
                e.stopPropagation();
            } },
            react_1.default.createElement(CustomSelectComp, { active: showActions, onClick: function () {
                    setShowActions(!showActions);
                } }))) : (react_1.default.createElement(ButtonComp, __assign({}, buttonSize, { icon: icon, iconSize: iconSize, invert: invert, stopPropagation: true, preventDefault: true, onClick: function (e) {
                setShowActions(!showActions);
            }, active: showActions })))),
        popOutOfOverflowHiddenParent && (react_1.default.createElement(styled_1.StyledActionMenuTracker, { storybookPreview: storybookPreview, align: align, ref: trackerRef })),
        popOutOfOverflowHiddenParent
            ? (0, react_dom_1.createPortal)(genLighbox(), document.getElementById("actionmenu-lightbox-root"))
            : genLighbox()));
};
exports.ActionMenu = ActionMenu;
